import type { TourSection } from '../contexts/TourContext';

/**
 * MMEC platform virtual tour — Arabic-first copy, targets use [data-tour="…"] hooks.
 * tabToChange: simple tab id (flow|scanner|gex|volume|elite_pulse) or advanced component id.
 */
export const MMEC_PLATFORM_TOUR_SECTIONS: TourSection[] = [
  {
    id: 'welcome',
    title: 'MMEC Platform Tour',
    titleAr: 'جولة منصة MMEC',
    description: 'Companion-guided tour of every major tool',
    descriptionAr: 'جولة بإرشاد المرافق لكل الأدوات الرئيسية',
    steps: [
      {
        id: 'welcome-1',
        title: 'Welcome',
        titleAr: 'مرحباً بك في MMEC',
        description:
          'This interactive tour walks through navigation, data modes, tabs, and the MMEC Companion. Use Next to move; you can exit anytime.',
        descriptionAr:
          'هذه الجولة التفاعلية تشرح التنقل وأوضاع البيانات والتبويبات ومرافق MMEC. استخدم «التالي» للمتابعة ويمكنك الخروج في أي وقت.',
        target: 'body',
        position: 'center',
        action: 'none',
        highlight: false,
        importance: 'Educational only — not financial advice.',
        importanceAr: 'للتعليم فقط — وليس نصيحة مالية.',
        tips: ['Tour starts after you accept the legal disclaimer'],
        tipsAr: ['تبدأ الجولة بعد الموافقة على إخلاء المسؤولية'],
      },
      {
        id: 'companion-intro',
        title: 'MMEC Companion',
        titleAr: 'مرافق MMEC',
        description:
          'Your Arabic AI companion explains pulse reports, highlights symbols on screen, and answers questions about the platform.',
        descriptionAr:
          'مرافقك الذكي يشرح تقارير النبض، يبرز الرموز على الشاشة، ويجيب عن أسئلة المنصة بالعربية.',
        target: '[data-tour="mmec-companion"]',
        position: 'left',
        action: 'none',
        highlight: true,
        importance: 'Best for: quick context, Elite Pulse brief, paper-wallet actions.',
        importanceAr: 'الأفضل لـ: سياق سريع، ملخص نبض النخبة، إجراءات المحفظة الورقية.',
        tips: ['Open the panel from the dock button at the bottom-right'],
        tipsAr: ['افتح اللوحة من زر المرافق أسفل يمين الشاشة'],
      },
    ],
  },
  {
    id: 'shell',
    title: 'Top bar & modes',
    titleAr: 'الشريط العلوي والأوضاع',
    description: 'Global controls',
    descriptionAr: 'التحكم العام',
    steps: [
      {
        id: 'top-bar',
        title: 'Top bar',
        titleAr: 'الشريط العلوي',
        description:
          'Logo, Simple/Advanced mode, data source (Stable vs Legacy), language, theme, and alerts.',
        descriptionAr:
          'الشعار، وضع بسيط/متقدم، مصدر البيانات (مستقر/قديم)، اللغة، السمة، والتنبيهات.',
        target: '[data-tour="mmec-top-bar"]',
        position: 'bottom',
        highlight: true,
        importance: 'Switch Simple for mobile-style tabs; Advanced for full tool menu.',
        importanceAr: 'البسيط: تبويبات موبايل. المتقدم: قائمة الأدوات الكاملة.',
        tips: ['Simple mode is ideal for daily flow + GEX + Elite Pulse'],
        tipsAr: ['الوضع البسيط مثالي للتدفق + جاما + نبض النخبة يومياً'],
      },
      {
        id: 'data-source',
        title: 'Data source',
        titleAr: 'مصدر البيانات',
        description:
          'Stable: Theta/CBOE accumulated chain (fetch symbols in the strip). Legacy RTL: scanner file on server.',
        descriptionAr:
          'مستقر: سلسلة Theta/CBOE المتراكمة (اجلب الرموز من الشريط). قديم RTL: ملف الماسح على الخادم.',
        target: '[data-tour="mmec-data-source"]',
        position: 'bottom',
        highlight: true,
        tabToChange: 'flow',
        importance: 'Indexes (SPXW) need a Stable fetch before GEX shows chain data.',
        importanceAr: 'المؤشرات (SPXW) تحتاج جلب مستقر قبل ظهور سلسلة الجاما.',
      },
      {
        id: 'stable-strip',
        title: 'Stable fetch bar',
        titleAr: 'شريط الجلب المستقر',
        description:
          'Pick underlyings, expiry range, strike band — then fetch. This loads real option chain data for scanner/GEX.',
        descriptionAr:
          'اختر الأصول، مدى الاستحقاق، نطاق سعر التنفيذ — ثم اجلب. يحمّل بيانات سلسلة حقيقية للماسح والجاما.',
        target: '[data-tour="mmec-stable-fetch"]',
        position: 'bottom',
        highlight: true,
        tips: ['For SPX GEX, fetch SPXW or SPX here first'],
        tipsAr: ['لجاما SPX، اجلب SPXW أو SPX هنا أولاً'],
      },
    ],
  },
  {
    id: 'simple-tabs',
    title: 'Simple mode tabs',
    titleAr: 'تبويبات الوضع البسيط',
    description: 'Five main workspaces',
    descriptionAr: 'خمس مساحات عمل',
    steps: [
      {
        id: 'tabs-overview',
        title: 'Bottom navigation',
        titleAr: 'التنقل السفلي',
        description: 'Five tabs: Flow, Scanner, GEX, Volume, Elite Pulse.',
        descriptionAr: 'خمسة تبويبات: التدفق، الماسح، الجاما، الحجم، نبض النخبة.',
        target: '[data-tour="mmec-bottom-tabs"]',
        position: 'top',
        highlight: true,
      },
      {
        id: 'tab-flow',
        title: 'Flow',
        titleAr: 'التدفق',
        description:
          'Live options flow — large prints, sweeps, bias. Best for: spotting unusual activity and timing.',
        descriptionAr:
          'تدفق الخيارات الحي — صفقات كبيرة، كسح، انحياز. الأفضل لـ: نشاط غير عادي والتوقيت.',
        target: '[data-tour="mmec-tab-flow"]',
        position: 'top',
        tabToChange: 'flow',
        highlight: true,
      },
      {
        id: 'tab-scanner',
        title: 'Scanner',
        titleAr: 'الماسح',
        description:
          'Sortable table of accumulated chain metrics. Best for: comparing symbols and filtering strikes/DTE.',
        descriptionAr:
          'جدول قابل للفرز لمقاييس السلسلة المتراكمة. الأفضل لـ: مقارنة الرموز وتصفية سعر التنفيذ/الأيام.',
        target: '[data-tour="mmec-tab-scanner"]',
        position: 'top',
        tabToChange: 'scanner',
        highlight: true,
      },
      {
        id: 'tab-gex',
        title: 'GEX chart',
        titleAr: 'مخطط الجاما',
        description:
          'Real-time GEX + price chart. Enter SPX/SPXW for index; chain uses SPXW, chart uses index levels.',
        descriptionAr:
          'جاما فورية + مخطط السعر. أدخل SPX/SPXW للمؤشر؛ السلسلة SPXW والمخطط بمستويات المؤشر.',
        target: '[data-tour="mmec-tab-gex"]',
        position: 'top',
        tabToChange: 'gex',
        highlight: true,
        tips: ['Badge shows LIVE vs DELAYED data source'],
        tipsAr: ['الشارة توضح مصدر البيانات مباشر أو متأخر'],
      },
      {
        id: 'tab-volume',
        title: 'Volume',
        titleAr: 'الحجم',
        description:
          'Heatmap / legacy volume tools depending on data source. Best for: strike×expiry volume picture.',
        descriptionAr:
          'خريطة حرارية / أدوات حجم حسب المصدر. الأفضل لـ: صورة الحجم عبر سعر التنفيذ والاستحقاق.',
        target: '[data-tour="mmec-tab-volume"]',
        position: 'top',
        tabToChange: 'volume',
        highlight: true,
      },
      {
        id: 'tab-elite-pulse',
        title: 'Elite Pulse',
        titleAr: 'نبض النخبة',
        description:
          'RTL Scanner: institutional flow imbalances as a heatmap or table, filterable by rel-vol, premium, moneyness and expiry.',
        descriptionAr:
          'ماسح RTL: اختلالات التدفق المؤسسي كخريطة حرارية أو جدول، مع فلاتر للحجم النسبي والبريميوم والقرب من السعر وتاريخ الانتهاء.',
        target: '[data-tour="mmec-tab-elite_pulse"]',
        position: 'top',
        tabToChange: 'elite_pulse',
        highlight: true,
      },
    ],
  },
  {
    id: 'advanced',
    title: 'Advanced mode',
    titleAr: 'الوضع المتقدم',
    description: 'Full tool rail',
    descriptionAr: 'قائمة الأدوات الكاملة',
    steps: [
      {
        id: 'nav-rail',
        title: 'Navigation rail',
        titleAr: 'قائمة التنقل',
        description:
          'All charts, heatmaps, GEX tools, and analysis pages. Click any item to open that tool.',
        descriptionAr:
          'كل المخططات والخرائط وأدوات الجاما. انقر أي عنصر لفتح الأداة.',
        target: '[data-tour="mmec-nav-rail"]',
        position: 'right',
        highlight: true,
        tips: ['Collapse the rail for more chart space'],
        tipsAr: ['اطوِ القائمة لمساحة أكبر للمخطط'],
      },
      {
        id: 'filter-rail',
        title: 'Filter rail',
        titleAr: 'لوحة المرشحات',
        description: 'Tool-specific filters appear here in Advanced mode.',
        descriptionAr: 'مرشحات كل أداة تظهر هنا في الوضع المتقدم.',
        target: '[data-tour="mmec-filter-rail"]',
        position: 'left',
        highlight: true,
      },
      {
        id: 'realtime-gex-adv',
        title: 'Real-Time GEX',
        titleAr: 'جاما فورية',
        description: 'Open from Charts → Real-Time GEX for full-screen index/equity GEX.',
        descriptionAr: 'من المخططات → جاما فورية لشاشة كاملة للمؤشر/السهم.',
        target: '[data-tour="mmec-nav-rail"]',
        position: 'right',
        tabToChange: 'realtime-gex',
        highlight: true,
      },
    ],
  },
  {
    id: 'finish',
    title: 'Finish',
    titleAr: 'انتهت الجولة',
    description: 'You are ready to explore',
    descriptionAr: 'أنت جاهز للاستكشاف',
    steps: [
      {
        id: 'finish-1',
        title: 'Tour complete',
        titleAr: 'اكتملت الجولة',
        description:
          'Restart anytime from the companion panel or ask the companion “أرني الجولة”. Trade at your own risk.',
        descriptionAr:
          'أعد الجولة من لوحة المرافق أو اسأله «أرني الجولة». التداول على مسؤوليتك الخاصة.',
        target: 'body',
        position: 'center',
        highlight: false,
        tips: ['Companion highlights symbols when you tap chips in its brief'],
        tipsAr: ['المرافق يبرز الرموز عند النقر على الشرائح في ملخصه'],
      },
    ],
  },
];

export const MMEC_TOUR_SESSION_DONE_KEY = 'mmec-platform-tour-completed';
export const MMEC_TOUR_AUTO_START_KEY = 'mmec-platform-tour-autostart';
